import React, { createContext, useContext, useState, useEffect, ReactNode, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client'; // Import supabase client
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js';

// Use Supabase User type
interface AdminAuthContextType {
  user: User | null; // Changed from adminUser
  session: Session | null; // Changed from adminSession
  isAuthenticated: boolean; // Based on Supabase session
  isAdmin: boolean; // For now, isAuthenticated implies isAdmin, might need refinement
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>; // Changed username to email
  logout: () => Promise<void>; // Changed signature
}

const AdminAuthContext = createContext<AdminAuthContextType>({
  user: null,
  session: null,
  isAuthenticated: false,
  isAdmin: false,
  isLoading: true,
  login: async () => false,
  logout: async () => {},
});

interface AdminAuthProviderProps {
  children: ReactNode;
}

const AdminAuthProviderComponent: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check Supabase session on initial load and listen for changes
  useEffect(() => {
    console.log('AUTH: useEffect running - Setting up listener and getting session...');
    let isMounted = true; // Flag to prevent state updates on unmounted component

    try {
      const { data: authListener } = supabase.auth.onAuthStateChange(
        async (event: AuthChangeEvent, session: Session | null) => {
          // Only update state if the component is still mounted
          if (!isMounted) {
              console.log('AUTH: onAuthStateChange fired on unmounted component. Ignoring.');
              return;
          }
          console.log(`AUTH: Supabase auth event received: ${event}`, session);
          setSession(session);
          setUser(session?.user ?? null);
          // Ensure loading is set to false once we get the first event (like INITIAL_SESSION)
          if (isLoading) {
            console.log('AUTH: Setting isLoading to false due to auth event.');
            setIsLoading(false);
          }

          // Clear old localStorage data if found (migration step)
          if (localStorage.getItem('adminUser')) {
            localStorage.removeItem('adminUser');
            localStorage.removeItem('adminSession');
            console.log('AUTH: Removed old localStorage auth data.');
          }
        }
      );
       console.log('AUTH: onAuthStateChange listener attached.');

      // Initial check
      console.log('AUTH: Calling getSession()...');
      supabase.auth.getSession()
        .then(({ data, error }) => {
          if (!isMounted) {
             console.log('AUTH: getSession() completed on unmounted component. Ignoring.');
             return;
          }
          if (error) {
            console.error('AUTH: Error in getSession():', error);
            // Don't set state here if listener will handle INITIAL_SESSION
          } else {
            console.log('AUTH: getSession() successful. Data:', data);
            // Setting state here might be redundant if INITIAL_SESSION event fires,
            // but can help if the event is missed or delayed.
            // Only set state if the session is different from current state to avoid loops
            if (session?.access_token !== data.session?.access_token) {
               console.log('AUTH: Session from getSession() differs, updating state.');
               setSession(data.session);
               setUser(data.session?.user ?? null);
            }
          }
          // Crucially, set loading to false *after* the initial check attempt
          // regardless of whether the listener has fired yet.
          if (isLoading) {
             console.log('AUTH: Setting isLoading to false after getSession() attempt.');
             setIsLoading(false);
          }
        })
        .catch(catchError => {
           if (!isMounted) return;
           console.error('AUTH: Exception during getSession():', catchError);
           if (isLoading) {
              console.log('AUTH: Setting isLoading to false after getSession() exception.');
              setIsLoading(false); // Unblock UI even on error
           }
        });

      return () => {
        console.log('AUTH: useEffect cleanup - Unsubscribing listener.');
        isMounted = false; // Set flag on unmount
        if (authListener && typeof authListener.subscription?.unsubscribe === 'function') {
          authListener.subscription.unsubscribe();
        } else {
          console.warn("AUTH: Could not unsubscribe from auth listener.");
        }
      };

    } catch (setupError) {
       console.error('AUTH: Error setting up auth listener or getSession:', setupError);
       // Ensure loading is false if setup fails
       if (isMounted && isLoading) {
          console.log('AUTH: Setting isLoading to false due to setup error.');
          setIsLoading(false);
       }
       return; // Return undefined as cleanup is not needed if setup failed
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  // Função de login usando Supabase Auth
  const login = async (email: string, password: string): Promise<boolean> => {
    console.log(`AUTH: Attempting Supabase login for email: ${email}`);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) {
        console.error('AUTH: Supabase login error:', error);
        return false;
      }

      console.log('AUTH: Supabase login successful.');
      return true;
    } catch (error) {
      console.error('AUTH: Exception during Supabase login:', error);
      return false;
    }
    // Note: isLoading state is handled by the auth listener upon sign-in event
  };

  // Função de logout usando Supabase Auth
  const logout = async () => {
    // ... (logout function remains mostly the same, isLoading handled by listener)
    console.log('AUTH: Attempting Supabase logout...');
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('AUTH: Supabase logout error:', error);
    }
    // State updates handled by onAuthStateChange
    // Navigation should happen after state update confirms logout
     navigate('/admin/login'); // Navigate immediately or wait for listener?
     console.log('AUTH: Navigation to /admin/login triggered after logout call.');
  };

  const [isAdmin, setIsAdmin] = useState(false);
  const isAuthenticated = !!session && !!user;

  // Check if user is in admin_users table
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('admin_users')
          .select('is_active')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('AUTH: Error checking admin status:', error);
          setIsAdmin(false);
          return;
        }

        setIsAdmin(data?.is_active === true);
        console.log('AUTH: Admin status checked:', data?.is_active === true);
      } catch (error) {
        console.error('AUTH: Exception checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  const value = {
    user,
    session,
    isAuthenticated,
    isAdmin,
    isLoading,
    login,
    logout,
  };

   useEffect(() => {
    console.log('AUTH STATE CHANGED (Supabase): isAuthenticated:', isAuthenticated, 'isAdmin:', isAdmin, 'isLoading:', isLoading);
  }, [isAuthenticated, isAdmin, isLoading]);

  return (
    <AdminAuthContext.Provider value={value}>
      {/* Render children only when loading is finished */}
      {!isLoading ? children : (
          <div className="flex items-center justify-center min-h-screen bg-gray-100">
             <div>Carregando autenticação...</div>
          </div>
      )}
    </AdminAuthContext.Provider>
  );
};

export const AdminAuthProvider = memo(AdminAuthProviderComponent);

export const useAdminAuth = () => useContext(AdminAuthContext);

export default AdminAuthContext;
